import React from "react";
import { List } from "@refinedev/mui";
import { Tab, Tabs } from "@mui/material";
import { Grid2 as Grid } from "@mui/material";
import BarChartIcon from "@mui/icons-material/BarChart";
import AttachMoneyIcon from "@mui/icons-material/AttachMoney";
import StoreIcon from "@mui/icons-material/Store";
import CalendarMonthIcon from "@mui/icons-material/CalendarMonth";

import { DataGrid } from "@shared/components/DataGrid";
import {
  CsvExportButton,
  FilterDropdown,
  BarChart,
  SummaryCard,
} from "@shared/components";
import { UsageListFilters, UsagePeriodFilter } from "@modules/usages/components";
import { useUsageAnalyticsQuery } from "@modules/usages/services/queries";
import { formatCurrency } from "@core/utils/masks";
import { useQueryParams } from "@shared/hooks";

import { useUsagesList } from "./controller";
import { useUsagesListColumns } from "./columns";

export const UsageList = React.memo(() => {
  const {
    rows = [],
    loading,
    selectedRows,
    dataToExport,
    companies,
    agreements,
    users,
    discounts,
    setSelectedRows,
    refetch,
    translate,
    tableProps,
    filters,
    analyticsFilters,
    kind,
  } = useUsagesList();
  const { setQueryParams, getQueryParams } = useQueryParams();
  const { data: analytics, isLoading: analyticsLoading } =
    useUsageAnalyticsQuery(analyticsFilters);

  const tab = (["cinema", "local", "online"] as const).includes(
    kind as "cinema" | "local" | "online"
  )
    ? kind
    : "cinema";

  const columns = useUsagesListColumns({
    onRefetch: refetch,
    translate,
    companies,
    agreements,
    users,
    discounts,
  });

  return (
    <List
      headerButtons={
        <>
          <FilterDropdown
            initialValues={{
              company: "",
              state: "",
              city: "",
              user: "",
              discount: "",
              discount__mode: "",
              discount__type: "",
              payment_status: "",
            }}
          >
            <UsageListFilters />
          </FilterDropdown>

          <CsvExportButton
            data={dataToExport}
            filename="usages.csv"
            isLoading={loading}
            isDisabled={!rows?.length}
          />
        </>
      }
    >
      <UsagePeriodFilter />

      <Tabs
        value={tab}
        onChange={(_, value: string) => {
          setQueryParams({ ...getQueryParams(), tab: value, kind: value, page: "1" });
        }}
        variant="scrollable"
        sx={{ mb: 2 }}
      >
        <Tab value="cinema" label={translate("v1/usage_admin.tabs.cinema")} />
        <Tab value="local" label={translate("v1/usage_admin.tabs.local")} />
        <Tab value="online" label={translate("v1/usage_admin.tabs.online")} />
      </Tabs>

      <Grid container spacing={2} sx={{ mb: 2 }}>
        <Grid size={{ xs: 12, md: 3 }}>
          <SummaryCard
            icon={<BarChartIcon />}
            label={translate("v1/usage_admin.kpis.count")}
            value={analytics?.kpis.count.toLocaleString("pt-BR") || "0"}
            loading={analyticsLoading}
          />
        </Grid>
        <Grid size={{ xs: 12, md: 3 }}>
          <SummaryCard
            icon={<CalendarMonthIcon />}
            label={translate("v1/usage_admin.kpis.month_count")}
            value={analytics?.kpis.month_count.toLocaleString("pt-BR") || "0"}
            loading={analyticsLoading}
          />
        </Grid>
        <Grid size={{ xs: 12, md: 3 }}>
          <SummaryCard
            icon={<AttachMoneyIcon />}
            label={translate("v1/usage_admin.kpis.saved")}
            value={formatCurrency(analytics?.kpis.saved)}
            loading={analyticsLoading}
          />
        </Grid>
        <Grid size={{ xs: 12, md: 3 }}>
          <SummaryCard
            icon={<StoreIcon />}
            label={translate("v1/usage_admin.kpis.month_saved")}
            value={formatCurrency(analytics?.kpis.month_saved)}
            loading={analyticsLoading}
          />
        </Grid>
        <Grid size={{ xs: 12 }}>
          <BarChart
            title={translate("v1/usage_admin.charts.monthly")}
            xAxis={[
              {
                scaleType: "band",
                data:
                  analytics?.monthly.map((item) =>
                    ["Jan", "Fev", "Mar", "Abr", "Mai", "Jun", "Jul", "Ago", "Set", "Out", "Nov", "Dez"][
                      item.month - 1
                    ]
                  ) || [],
              },
            ]}
            series={[
              {
                label: translate("v1/usage_admin.kpis.count"),
                data: analytics?.monthly.map((item) => item.count) || [],
              },
            ]}
            height={320}
          />
        </Grid>
        <Grid size={{ xs: 12, md: 6 }}>
          <BarChart
            title={translate("v1/usage_admin.charts.top_companies")}
            layout="horizontal"
            yAxis={[
              {
                scaleType: "band",
                data: analytics?.top_companies.map((item) => item.name || "-") || [],
                tickLabelStyle: { fontSize: 10 },
              },
            ]}
            series={[{ data: analytics?.top_companies.map((item) => item.count) || [] }]}
            height={320}
          />
        </Grid>
        <Grid size={{ xs: 12, md: 6 }}>
          <BarChart
            title={translate("v1/usage_admin.charts.top_agreements")}
            layout="horizontal"
            yAxis={[
              {
                scaleType: "band",
                data: analytics?.top_agreements.map((item) => item.name || "-") || [],
                tickLabelStyle: { fontSize: 10 },
              },
            ]}
            series={[{ data: analytics?.top_agreements.map((item) => item.count) || [] }]}
            height={320}
          />
        </Grid>
      </Grid>

      <DataGrid
        {...tableProps}
        rows={rows}
        columns={columns}
        loading={loading}
        checkboxSelection
        rowSelectionModel={selectedRows}
        onRowSelectionModelChange={(selectionModel) =>
          setSelectedRows(selectionModel as number[])
        }
      />
    </List>
  );
});
